import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "styled-components";
import { AuthProvider } from "./auth/AuthContext";
import { queryClient } from "./api/queryClient";
import { AppRoutes } from "./routes";
import { GlobalStyles } from "@styles/GlobalStyles";
import { appTheme } from "@styles/Theme";
import { tenant } from "@/theme/tenant";
import { applyTenantTheme } from "@/theme/applyTenantTheme";
import moment from "moment";
import "moment/dist/locale/sv";
moment.locale("sv");
import "./index.css";

/**
 * Chakra needs a 100-900 scale per colour. The app only ever uses one shade, so
 * every step is the same colour - shading is done with `colord` where needed.
 */
function singleColorScale(color: string) {
	return Object.fromEntries([100, 200, 300, 400, 500, 600, 700, 800, 900].map((step) => [step, color]));
}

const chakraTheme = extendTheme({
	colors: {
		brand: singleColorScale(tenant.colors.primary),
		brandSecondary: singleColorScale(tenant.colors.secondary),
		accent: singleColorScale(tenant.colors.accent),
		success: {
			100: "#4BB769",
			200: "#4BB769",
			300: "#4BB769",
			400: "#4BB769",
			500: "#4BB769",
			600: "#4BB769",
			700: "#4BB769",
			800: "#4BB769",
			900: "#4BB769",
		},
		origoGreen: {
			50: "#e1f7e7",
			100: "#C0E6CA",
			200: "#A2DAB2",
			300: "#85CE9A",
			400: "#68C381",
			500: "#4BB769", // default
			600: "#3A9052",
			700: "#2A683B",
			800: "#1A4024",
			900: "#0A180E",
			950: "#020402",
		},
		muted: {
			50: "#F7F7F7",
			100: "#EDEDED",
			200: "#DEDEDE",
			300: "#CFCFCF",
			400: "#B1B1B1",
			500: "#A2A2A2",
			600: "#999",
			700: "#7A7A7A",
			800: "#5C5C5C",
			900: "#3D3D3D",
		},
		divider: "#bbb",
		dividerLight: "#DEDEDE",
	},
});

applyTenantTheme(tenant);

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<BrowserRouter>
			<QueryClientProvider client={queryClient}>
				<ChakraProvider theme={chakraTheme}>
					<ThemeProvider theme={appTheme}>
						{/* <GlobalStyles /> */}
						<AuthProvider>
							<AppRoutes />
						</AuthProvider>
					</ThemeProvider>
				</ChakraProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</BrowserRouter>
	</StrictMode>
);
